import React, { forwardRef, useCallback } from 'react';

import { useLiveQuery } from 'dexie-react-hooks';

import { BROWSABLE_CHANNEL_KINDS } from '../../lib/channels';
import db from '../../lib/db';
import { sortByRelevance } from '../../lib/sortByRelevance';
import { escapeRegex } from '../../lib/stringUtils';

import { useRouteWorkspace } from '../../contexts/RouteWorkspaceContext';
import { ChannelEntity } from '../ChannelEntity';
import { SuggestionList } from './SuggestionList';

export const ChannelList = forwardRef(({ command, query }, ref) => {
  const { workspaceId, mainChannelId, workspace } = useRouteWorkspace();

  const items = useLiveQuery(
    async function fetchChannels() {
      // An empty query (bare "#") matches everything and takes the default-order path below.
      const regex = new RegExp(escapeRegex(query || ''), 'i');
      const channels = await db.channels
        .where('[workspace_id+kind]')
        .anyOf(BROWSABLE_CHANNEL_KINDS.map((kind) => [workspaceId, kind]))
        .filter(function matchChannel(c) {
          if (!c?.name) return false;
          if (mainChannelId && String(c.id) === String(mainChannelId)) return false;
          return regex.test(c.name);
        })
        .limit(20)
        .toArray();

      const validChannels = channels.filter((c) => !!c?.name);

      // The main channel displays as the workspace title (ChannelEntity's home-row
      // rule), so it matches and ranks by that title — never by its hidden name.
      let mainEntry = null;
      if (mainChannelId && workspace?.title && regex.test(workspace.title)) {
        const mainChannel = await db.channels.get([workspaceId, mainChannelId]);
        if (mainChannel) mainEntry = { ...mainChannel, name: workspace.title };
      }

      if (!query) {
        // Bare "#": main chat first, then channels A→Z.
        validChannels.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        return [...(mainEntry ? [mainEntry] : []), ...validChannels].slice(0, 5);
      }

      if (mainEntry) validChannels.push(mainEntry);

      const sorted = sortByRelevance(validChannels, query, 'name', {
        wordBoundaryPattern: /[\s\-_]+/,
      });

      return sorted.slice(0, 5);
    },
    [query, mainChannelId, workspace, workspaceId],
    [],
  );

  const handleSelect = useCallback(
    function handleSelectCallback(channel) {
      command(channel);
    },
    [command],
  );

  const renderItem = useCallback(function renderChannelItem(channel) {
    return <ChannelEntity channel={channel} suggestion={true} />;
  }, []);

  return <SuggestionList ref={ref} items={items} onSelect={handleSelect} renderItem={renderItem} query={query} />;
});

ChannelList.displayName = 'ChannelList';
